import { useRef, useEffect, useState } from 'react';
import PalmerSectionLabel from './PalmerSectionLabel';

const revealText = "Blending design and code with functional clarity and creative precision. Delivering thoughtful digital systems with structure, flow, and expressive interaction.";

export default function PalmerAbout() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const words = revealText.split(' ');

  return (
    <section ref={sectionRef} className="bg-black py-20">
      <PalmerSectionLabel
        left1="© Personal Profile プロフィール"
        left2="(WDX® — 05)"
        right="Visual Thinker"
      />

      <div className="px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT: word-by-word reveal text + CTA */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            {/* Word-by-word reveal */}
            <div className="mb-10">
              <p
                className="font-semibold leading-tight"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(20px, 2.2vw, 32px)',
                  letterSpacing: '-0.02em',
                }}
              >
                {words.map((word, i) => (
                  <span
                    key={i}
                    className="inline-block mr-[0.3em] transition-all duration-500"
                    style={{
                      color: visible ? 'white' : 'rgba(255,255,255,0.08)',
                      transitionDelay: `${i * 35}ms`,
                      transform: visible ? 'translateY(0)' : 'translateY(6px)',
                    }}
                  >
                    {word}
                  </span>
                ))}
              </p>
            </div>

            <div className="flex flex-col gap-5">
              <p className="text-white/35 text-sm leading-relaxed max-w-md" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                We bridge creative direction with real-world execution, combining design and development into one{' '}
                <strong className="text-white/55">seamless workflow</strong> to deliver digital experiences that are thoughtful, fast, and built to perform.
              </p>
              <a
                href="/work"
                className="inline-flex items-center border border-white/30 text-white text-xs px-5 py-2.5 hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap self-start"
                style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.1em' }}
              >
                SEE WORKS
              </a>
            </div>

            {/* Partner logos */}
            <div className="mt-10 flex flex-wrap gap-3">
              {[
                'https://framerusercontent.com/images/liDp6RqOmZpoiyriU2da9i9ZRNM.png',
                'https://framerusercontent.com/images/6IX9srHugK666NPKQJythMbMME.png',
                'https://framerusercontent.com/images/np97j2F8KUZ2HLiullJ6eZCAlsQ.png',
              ].map((src, i) => (
                <div
                  key={i}
                  className="border border-white/10 px-4 py-2 flex items-center justify-center"
                  style={{ height: 40 }}
                >
                  <img src={src} alt="Partner" className="max-h-full object-contain opacity-35" style={{ maxWidth: 80 }} />
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: two stacked images */}
          <div className="lg:col-span-5 flex gap-4 items-start">
            <div className="flex-1 overflow-hidden" style={{ height: 500 }}>
              <img
                src="https://framerusercontent.com/images/5emb8oOwafbQsfWVIaaZ12WcTM.png"
                alt="About"
                className={`w-full h-full object-cover object-top transition-all duration-1000 ${visible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
              />
            </div>
            <div className="flex-1 overflow-hidden mt-14" style={{ height: 500 }}>
              <img
                src="https://framerusercontent.com/images/tFXdT1GAWfzky0TCheIFtJR4O3I.png"
                alt="About 2"
                className={`w-full h-full object-cover object-top transition-all duration-1000 delay-200 ${visible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
